import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CreditCard } from '../data/shared/CreditCard';

type CategorySelection = (string[][][] | null)[];

/**
 * Compact URL encoding for per-card category selections.
 *
 * Delimiters (none appear in category name strings):
 *   ,  separates card entries        2:travel,6:dining
 *   :  separates card-index from data
 *   ~  separates rates               travel~gas
 *   -  separates slots within a rate  travel-dining
 *   !  separates categories in a slot dining.takeoutDelivery.ubereats!dining.takeoutDelivery.doordash
 *
 * Example: ?cat=6:travel-dining.takeoutDelivery~gas.cenex
 */
function encodeCategories(categories: CategorySelection): string {
  const parts: string[] = [];
  for (let i = 0; i < categories.length; i++) {
    const cat = categories[i];
    if (!cat || cat.length === 0) {
      continue;
    }
    const ratesEncoded = cat
      .map((rate) =>
        rate.map((slot) => slot.join('!')).join('-')
      )
      .join('~');
    if (ratesEncoded) {
      parts.push(`${i}:${ratesEncoded}`);
    }
  }
  return parts.join(',');
}

function decodeCategories(encoded: string): CategorySelection {
  if (!encoded) {
    return [];
  }
  const result: CategorySelection = [];
  try {
    for (const entry of encoded.split(',')) {
      const colonIdx = entry.indexOf(':');
      if (colonIdx === -1) {
        continue;
      }
      const index = parseInt(entry.slice(0, colonIdx), 10);
      if (isNaN(index) || index < 0) {
        continue;
      }
      const data = entry.slice(colonIdx + 1);
      if (!data) {
        continue;
      }
      const rates: string[][][] = data.split('~').map((ratePart) =>
        ratePart.split('-').map((slotPart) =>
          slotPart.split('!').filter(Boolean)
        )
      );
      while (result.length <= index) {
        result.push(null);
      }
      result[index] = rates;
    }
  } catch {
    return [];
  }
  return result;
}

/**
 * Manages per-instance category selections, persisted to the `cat` URL param.
 * Returns handlers that keep selectedCards and categories in sync.
 */
export const useWalletCategories = (
  selectedCards: CreditCard[],
  setSelectedCards: Dispatch<SetStateAction<CreditCard[]>>
) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [rawCategories, setRawCategories] = useState<CategorySelection>(
    () => {
      const catParam = searchParams.get('cat');
      return catParam ? decodeCategories(catParam) : [];
    }
  );

  const selectedCardsRef = useRef(selectedCards);
  selectedCardsRef.current = selectedCards;

  // Reset categories on external card changes (saved wallet load, template select)
  // But preserve URL-sourced categories on the first card load (e.g. /w/:code?cat=...)
  const isInternalChange = useRef(false);
  const isFirstCardLoad = useRef(true);
  const rawCategoriesRef = useRef(rawCategories);
  rawCategoriesRef.current = rawCategories;
  const prevCardsRef = useRef(selectedCards);
  useEffect(() => {
    if (prevCardsRef.current !== selectedCards) {
      prevCardsRef.current = selectedCards;
      if (!isInternalChange.current) {
        // On the first card load, preserve categories that came from the URL
        if (isFirstCardLoad.current && rawCategoriesRef.current.length > 0) {
          isFirstCardLoad.current = false;
        } else {
          setRawCategories([]);
        }
      }
      isInternalChange.current = false;
    }
  }, [selectedCards]);

  // Sync categories → URL
  useEffect(() => {
    const encoded = encodeCategories(rawCategories);
    const current = searchParams.get('cat');

    // Skip if URL already matches
    if (current === (encoded || null)) {
      return;
    }

    if (encoded) {
      searchParams.set('cat', encoded);
    } else {
      searchParams.delete('cat');
    }
    setSearchParams(searchParams, { replace: true });
  }, [rawCategories, searchParams, setSearchParams]);

  const categoriesByIndex = useMemo(
    () => selectedCards.map((_, i) => rawCategories[i] ?? undefined),
    [selectedCards, rawCategories]
  );

  const handleAddCard = useCallback(
    (newCard: CreditCard) => {
      isInternalChange.current = true;
      setSelectedCards((prev: CreditCard[]) => [...prev, newCard]);
    },
    [setSelectedCards]
  );

  const handleRemoveCard = useCallback(
    (cardToRemove: CreditCard, walletIndex?: number) => {
      isInternalChange.current = true;
      const index = walletIndex ?? selectedCardsRef.current.indexOf(cardToRemove);
      if (index === -1) {
        return;
      }
      setSelectedCards((prev: CreditCard[]) =>
        [...prev.slice(0, index), ...prev.slice(index + 1)]
      );
      setRawCategories((prev: CategorySelection) =>
        [...prev.slice(0, index), ...prev.slice(index + 1)]
      );
    },
    [setSelectedCards]
  );

  const handleRemoveAll = useCallback(() => {
    isInternalChange.current = true;
    setSelectedCards([]);
    setRawCategories([]);
  }, [setSelectedCards]);

  const handleSelectCategoriesForCard = useCallback(
    (card: CreditCard, selectedCategories: string[][][], walletIndex?: number) => {
      setRawCategories((prev: CategorySelection) => {
        const idx = walletIndex ?? selectedCardsRef.current.indexOf(card);
        if (idx === -1) {
          return prev;
        }
        const next = [...prev];
        while (next.length <= idx) {
          next.push(null);
        }
        next[idx] = selectedCategories;
        return next;
      });
    },
    []
  );

  return {
    categoriesByIndex,
    handleAddCard,
    handleRemoveCard,
    handleRemoveAll,
    handleSelectCategoriesForCard,
  };
};
